import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useCanManageParts, useCorporatePartCostActions } from 'corporate/hooks';
import { ICorporatePartCostListItem } from 'corporate/types';
import { Color, Variant } from 'theme/enums';

interface IProps {
  costs: ICorporatePartCostListItem[];
  buttonElement?: ReactElement;
  buttonLabel?: string;
  onRemoveSuccess?: () => void;
  disabled?: boolean;
  notificationPosition?: NotificationPosition;
}

export const RemoveCorporatePartCostButton = ({
  costs,
  buttonElement,
  buttonLabel,
  onRemoveSuccess,
  disabled = false,
  notificationPosition,
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();
  const canManageParts = useCanManageParts();

  const { removeCorporatePartCost } = useCorporatePartCostActions({
    selectionCount: costs.length,
    position: notificationPosition,
  });

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    const ids = costs.map(({ id }) => id);

    const { success } = await removeCorporatePartCost({ ids });

    if (!success) {
      return;
    }

    onRemoveSuccess?.();
  }, [costs, removeCorporatePartCost, onRemoveSuccess]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: disabled ? undefined : () => setIsConfirmationOpen(true),
      });
    }

    return (
      <Button
        text={buttonLabel ?? t('buttonLabelRemove')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
        disabled={disabled}
      />
    );
  }, [buttonElement, buttonLabel, disabled, t]);

  if (!canManageParts) {
    return null;
  }

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleRemovePartCosts', { count: costs.length })}
        message={t('messageConfirmRemovePartCosts', { count: costs.length })}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
